import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    inject,
    input,
    InputSignal,
    OnDestroy,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { IqMcInfoBadgeComponent } from '@dat/iq-mc-info';
import { get, isEmpty, isEqual, isNil, isNumber, round } from 'lodash';
import {
    CapacityMaps,
    EquipmentTypes,
    MciGaugeQueryPayload,
    MciGaugeQueryPayloadSchema,
    McQuery,
    McQuerySchema,
    TimeFrames,
} from '../models';
import { emptyMcQuery } from '../models/mc-gauge.constants';

@Component({
    selector: 'iq-mc-gauge-mci',
    standalone: true,
    imports: [
        CommonModule,
        DatiqTemplateModule,
        MatIconModule,
        IqMcInfoBadgeComponent,
    ],
    templateUrl: './iq-mc-gauge-mci.component.html',
    styleUrl: './iq-mc-gauge-mci.component.scss',
})
export class IqMcGaugeMciComponent implements OnDestroy {
    id: InputSignal<string> = input('');

    private _payload: WritableSignal<MciGaugeQueryPayload> = signal({
        id: '',
        score: 0,
        fetch: false,
        loaded: false,
        hasError: false,
        noData: false,
        capacity: '',
        capacityMap: {},
        geoCode: '',
        equipmentType: EquipmentTypes.Van,
    });

    infoBadgeQueryParams: WritableSignal<McQuery> = signal({
        ...emptyMcQuery,
    });

    loadedMci: Signal<boolean> = computed(
        () => {
            return this._payload().loaded;
        },
        {
            equal: isEqual,
        },
    );

    errorMci: Signal<boolean> = computed(
        () => {
            return this._payload().hasError;
        },
        {
            equal: isEqual,
        },
    );

    noDataMci: Signal<boolean> = computed(
        () => {
            return this._payload().noData;
        },
        {
            equal: isEqual,
        },
    );

    fetchedMci: Signal<boolean> = computed(
        () => {
            return this._payload().fetch;
        },
        {
            equal: isEqual,
        },
    );

    dataInfoMci: Signal<string> = computed(
        () => {
            const loaded = this.loadedMci();
            const fetched = this.fetchedMci();
            const noData = this.noDataMci();
            const hasError = this.errorMci();
            const geographySelected = this.geographySelected();
            if (!geographySelected) return '';
            if (!fetched && !loaded) return '*--*';
            if (noData) return '**no Data**';
            if (hasError) return '**Error loading MCI Data.**';

            const score = this._payload().score;
            const capacity = this._payload().capacity;

            return `**${score} MCI** ${capacity}`;
        },
        {
            equal: isEqual,
        },
    );

    steps: Signal<
        { color?: string; index?: number; high?: number; low?: number }[]
    > = computed(
        () => {
            const capacityMap = this._payload().capacityMap;
            const loaded = this._payload().loaded;
            const fetched = this._payload().fetch;

            if (fetched) {
                if (!loaded) return this.emptySteps();
                return Object.values(capacityMap);
            }
            return Object.values(CapacityMaps[this.equipmentType()]);
        },
        {
            equal: isEqual,
        },
    );

    gaugeMarkerPosition: Signal<number> = computed(
        () => {
            const score = this._payload().score;

            if (!isNumber(score)) return 0;

            const capacityMap = this._payload().capacityMap;
            const capacity = this._payload().capacity;
            const currentEquipmentRangeScore = get(capacityMap, [capacity], {
                high: 0,
                low: 0,
            });
            const offsetScore = score - currentEquipmentRangeScore.low;
            const offsetHigh =
                currentEquipmentRangeScore.high -
                currentEquipmentRangeScore.low;
            const percentPosition = round((offsetScore / offsetHigh) * 100);
            return percentPosition;
        },
        {
            equal: isEqual,
        },
    );

    gaugeMarkerPositionIndex: Signal<number> = computed(
        () => {
            const capacityMap = this._payload().capacityMap;
            const capacity = this._payload().capacity;
            const index = get(capacityMap, [capacity, 'index'], 0);

            return index;
        },
        {
            equal: isEqual,
        },
    );

    geographySelected: Signal<boolean> = computed(() => {
        const geoCode = this._payload().geoCode;
        return !isEmpty(geoCode) || !isNil(geoCode);
    });

    equipmentType: WritableSignal<EquipmentTypes> = signal(EquipmentTypes.Van);

    isMciUpdating: WritableSignal<boolean> = signal(false);

    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    constructor() {
        this._deregisterMap = this._bus.registerMap({
            updateMciQueryGauge: {
                callback: (payload: MciGaugeQueryPayload) => {
                    if (payload.id !== this.id()) return;

                    const test: any =
                        MciGaugeQueryPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn(
                            'MciGaugeQueryPayloadSchema Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }
                    this._payload.set(payload);
                },
            },
            updateMcQueryControls: {
                callback: (payload: any) => {
                    const test: any = McQuerySchema.safeParse(payload);

                    if (!test.success) {
                        console.warn('McQuery Invalid Payload');
                        console.log(test?.error?.issues);
                        return;
                    }
                    this.infoBadgeQueryParams.set(payload);
                    this.equipmentType.set(payload.equipmentType);
                },
            },
            updateMciBadge: {
                callback: () => {
                    this.isMciUpdating.set(true);
                },
            },
        });
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregisterMap);
    }

    private emptySteps(): {
        color: string;
        index: number;
        high: number;
        low: number;
    }[] {
        return [1, 2, 3, 4, 5, 6].map((_: never, i) => ({
            color: '#9CA5AF',
            index: i,
            high: 0,
            low: 0,
        }));
    }

    openMciPanel(): void {
        this.dispatchModalSegmentEvent();
        this._bus.dispatch({
            name: 'openMcModalMci',
            payload: { id: this.id() },
        });
    }

    dispatchModalSegmentEvent() {
        this._bus.dispatch({
            name: 'type:tracking:log',
            payload: {
                type: 'SEGMENT',
                eventType: 'TRACK',
                eventName: 'MCI Info Modal Displayed',
                interaction: 'open',
                props: {},
            },
        })
    }
}
